// Admin · Hero Drop uploader.
// Short mp4/webm → object storage → autoplayed on homepage hero with a
// linked product chip (title + price + Shop Now button). Only one drop is
// active at a time.
//
// Auth: reuses the community-storage admin token (same password).

import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "loadout.admin.community.token";
const MAX_BYTES = 30 * 1024 * 1024; // 30 MiB
const ALLOWED = new Set(["video/mp4", "video/webm", "video/quicktime"]);

function useToken() {
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const save = useCallback((t) => {
    setToken(t);
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
  }, []);
  const clear = useCallback(() => {
    setToken("");
    try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  }, []);
  return [token, save, clear];
}

function LoginPanel({ onLogin }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/community/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      if (!r.ok) { setErr(r.status === 401 ? "Wrong password." : `Login failed (${r.status}).`); return; }
      const j = await r.json();
      onLogin(j.token);
    } catch { setErr("Network error. Try again."); } finally { setBusy(false); }
  };

  return (
    <main data-testid="admin-hero-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-[420px] border border-white/15 bg-black">
        <div className="border-b border-white/10 px-6 py-6">
          <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
          <h1 className="mt-2 font-display text-[26px] uppercase">Hero Drop</h1>
          <p className="mt-3 font-sans text-[13px] text-white/60">
            Upload the hero video that plays on the homepage. Uses the same operator password as the community gallery.
          </p>
        </div>
        <div className="px-6 py-6 space-y-4">
          <input
            data-testid="admin-hero-login-password"
            type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Operator password"
            className="w-full bg-transparent border border-white/20 px-3 py-3 font-mono text-[13px] placeholder:text-white/30 focus:outline-none focus:border-[#FF9F1C]"
            autoFocus
          />
          {err && <p data-testid="admin-hero-login-error" className="font-mono text-[11px] text-red-400">{err}</p>}
          <button
            data-testid="admin-hero-login-submit"
            type="submit" disabled={busy}
            className="w-full border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </main>
  );
}

function UploadForm({ token, onUploaded, onExpired }) {
  const [file, setFile] = useState(null);
  const [handle, setHandle] = useState("");
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [cta, setCta] = useState("Shop Now");
  const [activate, setActivate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!ALLOWED.has(f.type)) { setErr(`Unsupported type: ${f.type || "unknown"}`); return; }
    if (f.size > MAX_BYTES) { setErr(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MiB, max 30 MiB)`); return; }
    setErr(""); setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Pick a video first."); return; }
    if (!handle.trim()) { setErr("Product handle required (e.g. aula-hero-68-hero68-he-series)."); return; }
    setErr(""); setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("product_handle", handle.trim().replace(/^\/?p\//, ""));
      form.append("product_title", title.trim());
      form.append("product_price_display", price.trim());
      form.append("cta_label", (cta || "Shop Now").trim());
      form.append("activate", activate ? "true" : "false");
      const r = await fetch(`${BACKEND}/api/admin/hero-drop/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!r.ok) {
        if (r.status === 401) {
          setErr("Session expired — please sign in again.");
          onExpired();
          return;
        }
        setErr(`Upload failed (${r.status}).`);
        return;
      }
      setFile(null); setHandle(""); setTitle(""); setPrice(""); setCta("Shop Now");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded();
    } catch { setErr("Network error."); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-white/15 bg-black p-6 space-y-5" data-testid="admin-hero-upload-form">
      <div>
        <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">Video file (mp4 / webm, max 30 MiB)</label>
        <input
          ref={inputRef} data-testid="admin-hero-file-input"
          type="file" accept="video/mp4,video/webm,video/quicktime"
          onChange={(e) => pick(e.target.files?.[0])}
          className="mt-2 block w-full text-[13px] text-white/80 file:mr-4 file:py-2 file:px-4 file:border file:border-[#FF9F1C] file:bg-transparent file:text-[#FF9F1C] file:font-mono file:text-[11px] file:tracking-[0.18em] file:uppercase file:cursor-pointer"
        />
        {file && (
          <p className="mt-2 font-mono text-[11px] text-white/50">
            {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MiB · {file.type}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Product handle (required)" value={handle} setValue={setHandle} placeholder="aula-hero-68-hero68-he-series" testid="admin-hero-handle" />
        <Field label="Product title" value={title} setValue={setTitle} placeholder="AULA HERO 68 HE" testid="admin-hero-title" />
        <Field label="Price display" value={price} setValue={setPrice} placeholder="₹5,999" testid="admin-hero-price" />
        <Field label="CTA label" value={cta} setValue={setCta} placeholder="Shop Now" testid="admin-hero-cta" />
      </div>

      <label className="flex items-center gap-2 font-mono text-[12px] text-white/70">
        <input
          type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)}
          data-testid="admin-hero-activate-toggle"
          className="w-4 h-4 accent-[#FF9F1C]"
        />
        Make this the active hero drop immediately (replaces current).
      </label>

      {err && <p data-testid="admin-hero-upload-error" className="font-mono text-[11px] text-red-400">{err}</p>}

      <button
        data-testid="admin-hero-upload-submit"
        type="submit" disabled={busy || !file}
        className="border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase px-6 py-3 disabled:opacity-50"
      >
        {busy ? "Uploading…" : "Upload & publish"}
      </button>
    </form>
  );
}

function Field({ label, value, setValue, placeholder, testid }) {
  return (
    <div>
      <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">{label}</label>
      <input
        data-testid={testid}
        value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder}
        className="mt-2 w-full bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white placeholder:text-white/25 focus:outline-none focus:border-[#FF9F1C]"
      />
    </div>
  );
}

function DropRow({ drop, token, onChanged, onExpired }) {
  const [busy, setBusy] = useState(false);

  const api = async (method, path, body) => {
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (body) headers["Content-Type"] = "application/json";
      const r = await fetch(`${BACKEND}${path}`, {
        method, headers, body: body ? JSON.stringify(body) : undefined,
      });
      if (r.status === 401) { onExpired(); return false; }
      if (r.ok) await onChanged();
      return r.ok;
    } finally { setBusy(false); }
  };

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const startEdit = () => {
    setForm({
      product_title: drop.product_title || "",
      product_handle: drop.product_handle || "",
      product_price_display: drop.product_price_display || "",
      cta_label: drop.cta_label || "Shop Now",
    });
    setEditing(true);
  };
  const [editErr, setEditErr] = useState("");
  const saveEdit = async () => {
    setEditErr("");
    const ok = await api("PATCH", `/api/admin/hero-drop/${drop.id}`, {
      ...form,
      product_handle: form.product_handle.trim().replace(/^\/?p\//, ""),
    });
    if (ok) setEditing(false);
    else if (ok === false) setEditErr("Couldn't save — check the fields and try again.");
  };
  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const activate = () => api("POST", `/api/admin/hero-drop/${drop.id}/activate`);
  const deactivate = () => api("POST", `/api/admin/hero-drop/${drop.id}/deactivate`);
  const remove = () => {
    if (!window.confirm("Delete this hero drop?")) return;
    api("DELETE", `/api/admin/hero-drop/${drop.id}`);
  };

  return (
    <div className="border border-white/10 bg-black p-4 flex flex-col lg:flex-row gap-4" data-testid="admin-hero-row">
      <video
        src={`${BACKEND}${drop.video_url}`} muted playsInline loop
        className="w-full lg:w-[260px] h-[160px] object-cover bg-black border border-white/10"
        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
        onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
        preload="metadata"
      />
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {drop.is_active && (
            <span data-testid="admin-hero-active-badge" className="font-mono text-[10px] tracking-[0.14em] uppercase bg-[#FF9F1C] text-[#0A0A0A] px-2 py-1">
              Live on home
            </span>
          )}
          <span className="font-mono text-[11px] text-white/40">
            {new Date(drop.uploaded_at).toLocaleString()}
          </span>
          <span className="font-mono text-[11px] text-white/40">
            {(drop.size_bytes / 1024 / 1024).toFixed(2)} MiB
          </span>
        </div>
        {editing ? (
          <div data-testid="admin-hero-edit-form" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Product title" value={form.product_title} setValue={setF("product_title")} testid="admin-hero-edit-title" />
            <Field label="Product handle" value={form.product_handle} setValue={setF("product_handle")} placeholder="atk-blazing-sky-phantom-ultimate" testid="admin-hero-edit-handle" />
            <Field label="Price display" value={form.product_price_display} setValue={setF("product_price_display")} placeholder="10499" testid="admin-hero-edit-price" />
            <Field label="CTA label" value={form.cta_label} setValue={setF("cta_label")} testid="admin-hero-edit-cta" />
            {editErr && <p data-testid="admin-hero-edit-error" className="sm:col-span-2 font-mono text-[11px] text-red-400">{editErr}</p>}
          </div>
        ) : (
          <>
            <p className="font-display text-[20px] uppercase text-white">{drop.product_title || "(untitled)"}</p>
            <p className="font-mono text-[12px] text-white/60">
              /p/{drop.product_handle || "—"} · {drop.product_price_display || "—"} · CTA: {drop.cta_label}
            </p>
          </>
        )}
        <div className="mt-auto flex gap-2 flex-wrap">
          {editing ? (
            <>
              <button data-testid="admin-hero-edit-save" onClick={saveEdit} disabled={busy} className="border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:bg-transparent hover:text-[#FF9F1C] disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
              <button data-testid="admin-hero-edit-cancel" onClick={() => setEditing(false)} disabled={busy} className="border border-white/30 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:border-white disabled:opacity-50">
                Cancel
              </button>
            </>
          ) : (
            <button data-testid="admin-hero-edit" onClick={startEdit} disabled={busy} className="border border-white/30 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:border-[#FF9F1C] hover:text-[#FF9F1C] disabled:opacity-50">
              Edit
            </button>
          )}
          {drop.is_active ? (
            <button data-testid="admin-hero-deactivate" onClick={deactivate} disabled={busy} className="border border-white/30 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:border-[#FF9F1C] hover:text-[#FF9F1C] disabled:opacity-50">
              Deactivate
            </button>
          ) : (
            <button data-testid="admin-hero-activate" onClick={activate} disabled={busy} className="border border-[#FF9F1C] text-[#FF9F1C] font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:bg-[#FF9F1C] hover:text-[#0A0A0A] disabled:opacity-50">
              Make Active
            </button>
          )}
          <button data-testid="admin-hero-delete" onClick={remove} disabled={busy} className="border border-red-500/40 text-red-400 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 hover:bg-red-500 hover:text-black disabled:opacity-50">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminHeroDrop() {
  const [token, saveToken, clearToken] = useToken();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/hero-drop/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { clearToken(); return; }
      const j = await r.json();
      setDrops(j.drops || []);
    } finally { setLoading(false); }
  }, [token, clearToken]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <LoginPanel onLogin={saveToken} />;

  return (
    <main data-testid="admin-hero-page" className="min-h-screen bg-[#0A0A0A] text-white px-6 py-12">
      <div className="mx-auto max-w-[1100px]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
            <h1 className="mt-2 font-display text-[32px] uppercase">Hero Drop</h1>
            <p className="mt-2 font-sans text-[13px] text-white/60 max-w-[64ch]">
              Upload a short mp4/webm. Mark it active and it plays as the homepage hero background with the product chip and Shop Now CTA you set below.
            </p>
          </div>
          <button data-testid="admin-hero-signout" onClick={clearToken} className="font-mono text-[11px] tracking-[0.18em] uppercase border border-white/25 px-4 py-2 hover:border-[#FF9F1C] hover:text-[#FF9F1C]">
            Sign out
          </button>
        </div>

        <section className="mt-10">
          <UploadForm token={token} onUploaded={load} onExpired={clearToken} />
        </section>

        <section className="mt-12">
          <h2 className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">All hero drops ({drops.length})</h2>
          <div className="mt-4 space-y-3" data-testid="admin-hero-list">
            {loading && <p className="font-mono text-[11px] text-white/40">Loading…</p>}
            {!loading && drops.length === 0 && (
              <p className="font-mono text-[11px] text-white/40">No hero drops yet. Upload one above.</p>
            )}
            {drops.map((d) => (
              <DropRow key={d.id} drop={d} token={token} onChanged={load} onExpired={clearToken} />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
